// pages/ReportsPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FileText, Download, Eye, Calendar, Filter, TrendingUp, BarChart3,
    PieChart, LineChart, Users, Package, Truck, DollarSign, Clock,
    AlertTriangle, CheckCircle, Settings, Plus, RefreshCw, Share2,
    Bookmark, Star, Grid, List, Search, ArrowRight, Target
} from 'lucide-react';
import { Card, Button, Badge, Input, Select, Modal } from '../components/ui';
import { formatDate } from '../utils/formatters';

const ReportsPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(false);
    const [selectedDateRange, setSelectedDateRange] = useState('month');
    const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
    const [viewMode, setViewMode] = useState('grid');
    const [searchQuery, setSearchQuery] = useState('');
    const [showReportBuilder, setShowReportBuilder] = useState(false);
    const [filters, setFilters] = useState({
        customer: '',
        status: '',
        jobType: '',
        vehicle: ''
    });

    // Mock report data
    const reportCategories = [
        {
            id: 'operational',
            name: 'Operational Reports',
            icon: Package,
            color: 'blue',
            reports: [
                {
                    id: 'job-summary',
                    name: 'Job Summary Report',
                    description: 'Comprehensive overview of all jobs with status and performance metrics',
                    lastRun: '2024-03-15T10:30:00',
                    frequency: 'Daily',
                    format: ['PDF', 'Excel', 'CSV'],
                    thumbnail: '/thumbnails/job-summary.png'
                },
                {
                    id: 'vehicle-utilization',
                    name: 'Vehicle Utilization Report',
                    description: 'Analysis of vehicle usage, efficiency, and maintenance schedules',
                    lastRun: '2024-03-15T09:15:00',
                    frequency: 'Weekly',
                    format: ['PDF', 'Excel'],
                    thumbnail: '/thumbnails/vehicle-utilization.png'
                },
                {
                    id: 'route-optimization',
                    name: 'Route Optimization Report',
                    description: 'Route efficiency analysis with recommendations for optimization',
                    lastRun: '2024-03-14T16:45:00',
                    frequency: 'Monthly',
                    format: ['PDF', 'Excel'],
                    thumbnail: '/thumbnails/route-optimization.png'
                }
            ]
        },
        {
            id: 'financial',
            name: 'Financial Reports',
            icon: DollarSign,
            color: 'green',
            reports: [
                {
                    id: 'revenue-analysis',
                    name: 'Revenue Analysis',
                    description: 'Detailed revenue breakdown by customer, route, and time period',
                    lastRun: '2024-03-15T08:00:00',
                    frequency: 'Monthly',
                    format: ['PDF', 'Excel', 'CSV'],
                    thumbnail: '/thumbnails/revenue-analysis.png'
                },
                {
                    id: 'cost-analysis',
                    name: 'Cost Analysis Report',
                    description: 'Comprehensive cost breakdown including fuel, maintenance, and operational costs',
                    lastRun: '2024-03-15T07:30:00',
                    frequency: 'Monthly',
                    format: ['PDF', 'Excel'],
                    thumbnail: '/thumbnails/cost-analysis.png'
                },
                {
                    id: 'detention-charges',
                    name: 'Detention Charges Report',
                    description: 'Analysis of detention charges, trends, and customer-wise breakdown',
                    lastRun: '2024-03-15T11:20:00',
                    frequency: 'Weekly',
                    format: ['PDF', 'Excel', 'CSV'],
                    thumbnail: '/thumbnails/detention-charges.png'
                },
                {
                    id: 'profit-margin',
                    name: 'Profit Margin Analysis',
                    description: 'Profit margin analysis by route, customer, and service type',
                    lastRun: '2024-03-14T14:10:00',
                    frequency: 'Monthly',
                    format: ['PDF', 'Excel'],
                    thumbnail: '/thumbnails/profit-margin.png'
                }
            ]
        },
        {
            id: 'performance',
            name: 'Performance Reports',
            icon: TrendingUp,
            color: 'purple',
            reports: [
                {
                    id: 'kpi-dashboard',
                    name: 'KPI Dashboard Report',
                    description: 'Key performance indicators with trends and benchmarks',
                    lastRun: '2024-03-15T12:00:00',
                    frequency: 'Daily',
                    format: ['PDF', 'Dashboard'],
                    thumbnail: '/thumbnails/kpi-dashboard.png'
                },
                {
                    id: 'delivery-performance',
                    name: 'Delivery Performance Report',
                    description: 'On-time delivery metrics, delays, and performance trends',
                    lastRun: '2024-03-15T09:45:00',
                    frequency: 'Weekly',
                    format: ['PDF', 'Excel', 'CSV'],
                    thumbnail: '/thumbnails/delivery-performance.png'
                },
                {
                    id: 'customer-satisfaction',
                    name: 'Customer Satisfaction Report',
                    description: 'Customer feedback analysis and satisfaction scores',
                    lastRun: '2024-03-14T15:30:00',
                    frequency: 'Monthly',
                    format: ['PDF', 'Excel'],
                    thumbnail: '/thumbnails/customer-satisfaction.png'
                }
            ]
        },
        {
            id: 'compliance',
            name: 'Compliance Reports',
            icon: CheckCircle,
            color: 'emerald',
            reports: [
                {
                    id: 'regulatory-compliance',
                    name: 'Regulatory Compliance Report',
                    description: 'Compliance status with transportation regulations and standards',
                    lastRun: '2024-03-14T13:15:00',
                    frequency: 'Monthly',
                    format: ['PDF'],
                    thumbnail: '/thumbnails/regulatory-compliance.png'
                },
                {
                    id: 'safety-report',
                    name: 'Safety & Incident Report',
                    description: 'Safety incidents, violations, and corrective actions taken',
                    lastRun: '2024-03-15T10:00:00',
                    frequency: 'Weekly',
                    format: ['PDF', 'Excel'],
                    thumbnail: '/thumbnails/safety-report.png'
                }
            ]
        }
    ];

    // Analytics data
    const analyticsData = {
        totalReports: 156,
        generatedToday: 12,
        scheduledReports: 8,
        averageGenerationTime: '2.3 min',
        mostUsedFormat: 'PDF (67%)',
        topCategories: [
            { name: 'Financial', percentage: 45, count: 70 },
            { name: 'Operational', percentage: 32, count: 50 },
            { name: 'Performance', percentage: 15, count: 23 },
            { name: 'Compliance', percentage: 8, count: 13 }
        ]
    };

    // Filter reports based on search and filters
    const filteredReports = useMemo(() => {
        let allReports = reportCategories.flatMap(category =>
            category.reports.map(report => ({ ...report, category: category.name, categoryId: category.id }))
        );

        if (searchQuery) {
            allReports = allReports.filter(report =>
                report.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                report.description.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        return allReports;
    }, [searchQuery]);

    const tabs = [
        { id: 'overview', label: 'Overview', icon: BarChart3 },
        { id: 'reports', label: 'All Reports', icon: FileText },
        { id: 'scheduled', label: 'Scheduled', icon: Clock },
        { id: 'analytics', label: 'Analytics', icon: TrendingUp },
        { id: 'builder', label: 'Report Builder', icon: Settings }
    ];

    const ReportCard = ({ report, category }) => (
        <Card className="group hover:shadow-lg transition-all duration-200 cursor-pointer">
            <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                        <div className={`p-2 rounded-lg bg-${category.color}-100 mr-3`}>
                            <FileText className={`w-5 h-5 text-${category.color}-600`} />
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                {report.name}
                            </h3>
                            <Badge variant="gray" size="sm" className="mt-1">
                                {category.name}
                            </Badge>
                        </div>
                    </div>
                    <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <Star className="w-4 h-4" />
                    </Button>
                </div>

                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {report.description}
                </p>

                <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Last Run:</span>
                        <span className="text-gray-900">{formatDate(report.lastRun, 'datetime')}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Frequency:</span>
                        <span className="text-gray-900">{report.frequency}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Formats:</span>
                        <div className="flex gap-1">
                            {report.format.map((fmt) => (
                                <Badge key={fmt} variant="gray" size="sm">{fmt}</Badge>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex gap-2 pt-4 border-t border-gray-100">
                    <Button variant="primary" size="sm" className="flex-1">
                        <Eye className="w-4 h-4 mr-2" />
                        View
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1">
                        <Download className="w-4 h-4 mr-2" />
                        Download
                    </Button>
                    <Button variant="outline" size="sm">
                        <Share2 className="w-4 h-4" />
                    </Button>
                </div>
            </div>
        </Card>
    );

    const OverviewTab = () => (
        <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card className="p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Total Reports</p>
                            <p className="text-2xl font-bold text-gray-900">{analyticsData.totalReports}</p>
                        </div>
                        <div className="p-3 bg-blue-100 rounded-lg">
                            <FileText className="w-6 h-6 text-blue-600" />
                        </div>
                    </div>
                </Card>

                <Card className="p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Generated Today</p>
                            <p className="text-2xl font-bold text-gray-900">{analyticsData.generatedToday}</p>
                        </div>
                        <div className="p-3 bg-green-100 rounded-lg">
                            <TrendingUp className="w-6 h-6 text-green-600" />
                        </div>
                    </div>
                </Card>

                <Card className="p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Scheduled Reports</p>
                            <p className="text-2xl font-bold text-gray-900">{analyticsData.scheduledReports}</p>
                        </div>
                        <div className="p-3 bg-yellow-100 rounded-lg">
                            <Clock className="w-6 h-6 text-yellow-600" />
                        </div>
                    </div>
                </Card>
            </div>

            <Card className="p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-600">Avg. Generation Time</p>
                        <p className="text-2xl font-bold text-gray-900">{analyticsData.averageGenerationTime}</p>
                    </div>
                    <div className="p-3 bg-purple-100 rounded-lg">
                        <Target className="w-6 h-6 text-purple-600" />
                    </div>
                </div>
            </Card>
        </div>
    );

    {/* Report Categories */ }
    <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Report Categories</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reportCategories.map((category) => {
                const IconComponent = category.icon;
                return (
                    <Card key={category.id} className="p-6 hover:shadow-md transition-shadow cursor-pointer">
                        <div className="text-center">
                            <div className={`inline-flex p-4 rounded-full bg-${category.color}-100 mb-4`}>
                                <IconComponent className={`w-8 h-8 text-${category.color}-600`} />
                            </div>
                            <h4 className="font-semibold text-gray-900 mb-2">{category.name}</h4>
                            <p className="text-sm text-gray-600 mb-4">{category.reports.length} reports available</p>
                            <Button variant="outline" size="sm" fullWidth>
                                View Reports
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    </Card>
                );
            })}
        </div>
    </div>

    {/* Recent Reports */ }
    <div>
        <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recently Generated</h3>
            <Button variant="outline" size="sm">
                View All
            </Button>
        </div>
        <div className="space-y-4">
            {filteredReports.slice(0, 5).map((report) => {
                const category = reportCategories.find(cat => cat.id === report.categoryId);
                return (
                    <Card key={report.id} className="p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className={`p-2 rounded-lg bg-${category.color}-100 mr-3`}>
                                    <FileText className={`w-4 h-4 text-${category.color}-600`} />
                                </div>
                                <div>
                                    <h4 className="font-medium text-gray-900">{report.name}</h4>
                                    <p className="text-sm text-gray-500">
                                        Generated {formatDate(report.lastRun, 'datetime')}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Badge variant="success" size="sm">Ready</Badge>
                                <Button variant="outline" size="sm">
                                    <Download className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </Card>
                );
            })}
        </div>
    </div>

    const ReportsTab = () => (
        <div className="space-y-6">
            {/* Filters and Search */}
            <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
                <div className="flex items-center gap-4 w-full lg:w-auto">
                    <Input
                        placeholder="Search reports..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        icon={Search}
                        className="w-full lg:w-80"
                    />
                    <Button variant="outline" size="sm">
                        <Filter className="w-4 h-4 mr-2" />
                        Filters
                    </Button>
                </div>

                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-1">
                        <Button
                            variant={viewMode === 'grid' ? 'primary' : 'ghost'}
                            size="sm"
                            onClick={() => setViewMode('grid')}
                        >
                            <Grid className="w-4 h-4" />
                        </Button>
                        <Button
                            variant={viewMode === 'list' ? 'primary' : 'ghost'}
                            size="sm"
                            onClick={() => setViewMode('list')}
                        >
                            <List className="w-4 h-4" />
                        </Button>
                    </div>

                    <Button variant="primary">
                        <Plus className="w-4 h-4 mr-2" />
                        New Report
                    </Button>
                </div>
            </div>

            {/* Reports Grid/List */}
            {reportCategories.map((category) => (
                <div key={category.id}>
                    <div className="flex items-center mb-4">
                        <div className={`p-2 rounded-lg bg-${category.color}-100 mr-3`}>
                            <category.icon className={`w-5 h-5 text-${category.color}-600`} />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                        <Badge variant="gray" size="sm" className="ml-2">
                            {category.reports.length}
                        </Badge>
                    </div>

                    <div className={`grid gap-6 ${viewMode === 'grid'
                        ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                        : 'grid-cols-1'
                        }`}>
                        {category.reports.map((report) => (
                            <ReportCard key={report.id} report={report} category={category} />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );

    const AnalyticsTab = () => (
        <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Usage by Category */}
                <Card className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage by Category</h3>
                    <div className="space-y-4">
                        {analyticsData.topCategories.map((category, index) => (
                            <div key={index} className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <div className="w-3 h-3 rounded-full bg-blue-500 mr-3" />
                                    <span className="text-sm font-medium text-gray-900">{category.name}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-24 bg-gray-200 rounded-full h-2">
                                        <div
                                            className="bg-blue-500 h-2 rounded-full"
                                            style={{ width: `${category.percentage}%` }}
                                        />
                                    </div>
                                    <span className="text-sm text-gray-600 w-12 text-right">
                                        {category.percentage}%
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                {/* Generation Trends */}
                <Card className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Generation Trends</h3>
                    <div className="h-64 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg flex items-center justify-center">
                        <div className="text-center">
                            <LineChart className="w-12 h-12 text-blue-400 mx-auto mb-2" />
                            <p className="text-gray-600">Chart showing report generation trends</p>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Detailed Analytics */}
            <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Report Performance Metrics</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-4 border border-gray-200 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">94%</div>
                        <div className="text-sm text-gray-600">Success Rate</div>
                    </div>
                    <div className="text-center p-4 border border-gray-200 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">2.3min</div>
                        <div className="text-sm text-gray-600">Avg. Generation Time</div>
                    </div>
                    <div className="text-center p-4 border border-gray-200 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600">847</div>
                        <div className="text-sm text-gray-600">Reports This Month</div>
                    </div>
                </div>
            </Card>
        </div>
    );

    return (
        <div className="p-6 space-y-6">
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                        <FileText className="w-6 h-6 mr-2 text-blue-600" />
                        Reports & Analytics
                    </h1>
                    <p className="text-gray-600 mt-1">
                        Generate comprehensive reports and analyze your logistics performance
                    </p>
                </div>

                <div className="flex items-center space-x-3 mt-4 lg:mt-0">
                    <Select
                        value={selectedDateRange}
                        onChange={(e) => setSelectedDateRange(e.target.value)}
                        options={[
                            { value: 'today', label: 'Today' },
                            { value: 'week', label: 'This Week' },
                            { value: 'month', label: 'This Month' },
                            { value: 'quarter', label: 'This Quarter' },
                            { value: 'year', label: 'This Year' },
                            { value: 'custom', label: 'Custom Range' }
                        ]}
                    />
                    <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                    </Button>
                    <Button variant="primary" onClick={() => setShowReportBuilder(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Create Report
                    </Button>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-gray-200">
                <nav className="flex space-x-8">
                    {tabs.map((tab) => {
                        const TabIcon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center">
                                    <TabIcon className="w-4 h-4 mr-2" />
                                    {tab.label}
                                </div>
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Tab Content */}
            <div>
                {activeTab === 'overview' && <OverviewTab />}
                {activeTab === 'reports' && <ReportsTab />}
                {activeTab === 'scheduled' && (
                    <Card className="p-8 text-center">
                        <Clock className="w-16 h-16 text-blue-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Scheduled Reports</h3>
                        <p className="text-gray-600">Manage and view your scheduled report generation.</p>
                    </Card>
                )}
                {activeTab === 'analytics' && <AnalyticsTab />}
                {activeTab === 'builder' && (
                    <Card className="p-8 text-center">
                        <Settings className="w-16 h-16 text-blue-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Report Builder</h3>
                        <p className="text-gray-600">Create custom reports with drag-and-drop interface.</p>
                    </Card>
                )}
            </div>

            {/* Report Builder Modal */}
            <Modal
                isOpen={showReportBuilder}
                onClose={() => setShowReportBuilder(false)}
                title="Create Custom Report"
                size="lg"
            >
                <div className="space-y-4">
                    <Input label="Report Name" placeholder="Enter report name" />
                    <Select
                        label="Report Type"
                        options={[
                            { value: 'operational', label: 'Operational' },
                            { value: 'financial', label: 'Financial' },
                            { value: 'performance', label: 'Performance' }
                        ]}
                    />
                    <div className="grid grid-cols-2 gap-4">
                        <Input label="Start Date" type="date" />
                        <Input label="End Date" type="date" />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                        <Button variant="outline" onClick={() => setShowReportBuilder(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary">
                            Create Report
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ReportsPage;